/**
 * prerequisiteQuizStepViewModel — la porte du quiz de prérequis et sa conversation en l'étape que
 * montre `<StudentPrerequisiteQuizPage>` : l'intro (« Commencer le quiz »), une question à la fois,
 * ou le résultat. Pur.
 *
 * La conversation (`useCheckpointConversation`, branchée par la porte sur l'API du quiz) reste le
 * moteur : elle garde chaque réponse, soumet la tentative à la dernière, donne le pétale et l'avis.
 * Ici, on n'en montre que le dernier tour : la question en cours (son rang est le nombre de réponses
 * déjà données), ou le résultat. La réponse choisie et pas encore envoyée est à l'écran.
 */
import { type PrerequisiteGateState, questionsOf } from "../../../../../engine/activity/prerequisite/prerequisiteGate"
import { type CheckpointConversationState } from "../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import type { StudentPrerequisiteQuizStep } from "../../../../../design-system/pages/StudentPrerequisiteQuizPage"
import { quizTextFormat } from "./quizTextFormat"

export function prerequisiteQuizStep(input: {
    gate: Pick<PrerequisiteGateState, "status" | "loading" | "quiz"> &
        Partial<Pick<PrerequisiteGateState, "reading" | "read">>
    checkpoint: Pick<
        CheckpointConversationState,
        "phase" | "messages" | "replies" | "answered" | "earned" | "feedback" | "feedbackSending"
    > | null
    selectedKey: string | null
}): StudentPrerequisiteQuizStep {
    const { gate, checkpoint, selectedKey } = input
    // Le quiz se lit avant l'intro : rien de la porte ne se montre encore, l'activité peut s'ouvrir.
    // « Pas encore lu » vaut une lecture : la lecture part juste après le premier rendu, et l'intro se
    // montrait le temps d'une image avant « vérification ».
    if (gate.status === "locked" && !gate.loading && (gate.reading === true || gate.read === false)) {
        return { kind: "checking" }
    }
    if (gate.status !== "quiz" || checkpoint == null) {
        return { kind: "intro", loading: gate.loading, failed: gate.status === "error" && !gate.loading }
    }

    const sam = checkpoint.messages.filter((message) => message.author === "sam")
    const current = sam[sam.length - 1]
    if (checkpoint.phase === "ended") {
        return {
            kind: "result",
            text: current?.text ?? "",
            answered: checkpoint.answered,
            earned: checkpoint.earned,
            feedback: checkpoint.feedback === 1 ? "positive" : checkpoint.feedback === -1 ? "negative" : null,
            feedbackSubmitting: checkpoint.feedbackSending,
        }
    }

    const options = checkpoint.replies.map((reply) => ({
        key: reply.key,
        label: reply.label,
        // Le rendu riche (une WebView sur téléphone) seulement pour une réponse à formule.
        ...(quizTextFormat(reply.label) === "rich" ? { labelFormat: "rich" as const } : {}),
    }))
    const text = current?.text ?? ""
    return {
        kind: "question",
        index: checkpoint.answered,
        total: questionsOf(gate.quiz).length,
        text,
        textFormat: quizTextFormat(text),
        options,
        selectedKey: options.some((option) => option.key === selectedKey) ? selectedKey : null,
        sending: checkpoint.phase === "sending" || checkpoint.phase === "opening",
        failed: checkpoint.phase === "error",
    }
}
